import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@proaxiv/ui'
import {
  useGetParticipantActivityQuery,
  type ActivityEntry,
  type GetParticipantActivityResponse,
} from '@proaxiv/server-api'
import { formatActivityMessage } from '../utils/formatActivityMessage'

// ── Icons ──────────────────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const ClockIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="text-pax-purple shrink-0"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
)

/** Formats an ISO timestamp for a conversation-history entry. */
function formatWhen(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(d)
}

interface Props {
  participantId: string
  participantName: string
  onClose: () => void
  /**
   * Rendered in the footer, below the history — the same action buttons already shown in this
   * participant's table row (e.g. accept/reject, resend, ask-for-presentation), so an organiser
   * doesn't have to close the conversation to act on it. Omit for tabs with no per-row actions.
   */
  actions?: ReactNode
}

/**
 * "See conversation" — a chronological log of every organiser communication and status
 * transition for one participant (invited, registered, accepted/rejected, presentation
 * request/upload/review, reminder, role change, …), with an optional slot for the row's normal
 * action buttons so the organiser can act without leaving the modal. See #158.
 */
export function ParticipantConversationModal({
  participantId,
  participantName,
  onClose,
  actions,
}: Props) {
  const { t, i18n } = useTranslation()
  const { data, isLoading, isError } = useGetParticipantActivityQuery({ participantId })
  const entries: ActivityEntry[] =
    (data as GetParticipantActivityResponse | undefined)?.entries ?? []

  return (
    <Modal labelledBy="conversation-modal-title">
      <div className="border-pax-purple/50 flex w-full max-w-lg flex-col rounded-2xl border bg-white shadow-2xl ring-1 ring-black/5">
        <div className="border-pax-purple/20 flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <ClockIcon />
            <div>
              <h2 id="conversation-modal-title" className="text-pax-black text-lg font-bold">
                {t('conversation_modal_title')}
              </h2>
              <p className="text-pax-gray text-sm">{participantName}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-pax-gray hover:text-pax-black cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none"
            aria-label={t('btn_close')}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto px-6 py-4">
          {isLoading && <p className="text-pax-gray py-6 text-center text-sm">{t('loading')}</p>}
          {isError && (
            <p className="text-pax-red py-6 text-center text-sm">{t('conversation_modal_error')}</p>
          )}
          {!isLoading && !isError && entries.length === 0 && (
            <p className="text-pax-gray py-6 text-center text-sm">
              {t('conversation_modal_empty')}
            </p>
          )}
          {!isLoading && !isError && entries.length > 0 && (
            <ol className="border-pax-purple/20 ml-1.5 flex flex-col gap-4 border-l pl-4">
              {entries.map((entry, idx) => (
                <li key={idx} className="relative">
                  <span
                    aria-hidden="true"
                    className="bg-pax-blue absolute top-1.5 -left-[21px] h-2 w-2 rounded-full"
                  />
                  <p className="text-pax-black text-sm font-medium">
                    {formatActivityMessage(entry, t)}
                  </p>
                  {entry.note && (
                    <p className="text-pax-gray mt-0.5 text-sm whitespace-pre-wrap">
                      &ldquo;{entry.note}&rdquo;
                    </p>
                  )}
                  <p className="text-pax-gray/70 mt-0.5 text-xs">
                    {formatWhen(entry.at, i18n.language)}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>

        {actions && (
          <div className="border-pax-purple/20 border-t px-6 py-4">
            <span className="text-pax-gray text-xs font-semibold uppercase">
              {t('conversation_modal_actions')}
            </span>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">{actions}</div>
          </div>
        )}
      </div>
    </Modal>
  )
}
